import React, { useContext, useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Stack,
  Tooltip,
  Divider,
  Grid,
  Switch,
  FormControlLabel,
} from "@mui/material";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import SettingsSuggestIcon from "@mui/icons-material/SettingsSuggest";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import ListAltIcon from "@mui/icons-material/ListAlt";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import TipsAndUpdatesIcon from "@mui/icons-material/TipsAndUpdates";
import PersonIcon from "@mui/icons-material/Person";
import SparkleIcon from "@mui/icons-material/AutoAwesome";
import RuleIcon from "@mui/icons-material/Rule";

import InstanceLeftSidebar from "../InstanceLeftSidebar";
import PageHeaderWithBreadcrumbs from "../../../components/PageHeaderWithBreadcrumbs";
import { AuthContext } from "../../../helpers/AuthContext";
import AiJobsTab from "./AiJobsTab";
import AiTriggersTab from "./AiTriggersTab";
import BrainTab from "./BrainTab";
import EzeeCapabilityPanel from "./EzeeCapabilityPanel";
import { canUseEzeeAI, isEzeeAdministrator } from "../../../utils/ezeeAccess";

import "./style.css";

const baseTabConfigs = [
  {
    key: "brain",
    label: "Ezee AI",
    icon: <SparkleIcon fontSize="small" />,
    requireEzee: true,
  },
  {
    key: "actions",
    label: "AI Actions",
    icon: <RocketLaunchIcon fontSize="small" />,
    requireAdmin: true,
  },
  {
    key: "settings",
    label: "AI Settings",
    icon: <SettingsSuggestIcon fontSize="small" />,
    requireAdmin: true,
  },
  {
    key: "triggers",
    label: "AI Triggers",
    icon: <RuleIcon fontSize="small" />,
    requireOps: true,
  },
  {
    key: "jobs",
    label: "AI Jobs",
    icon: <ListAltIcon fontSize="small" />,
    requireOps: true,
  },
];

const defaultAiActions = [
  {
    key: "lead-assignment",
    name: "New lead received",
    description:
      "Auto-triage inbound leads and assign the best client-rep for this instance.",
    defaultPrompt:
      "When a new lead is received, please assess which customer would be the best user in the Elite Core instance elite-promo-marketing, who is a client-rep to assign this to this lead, then email them.",
    status: "Enabled",
    scope: "Lead ingestion",
    trigger: "Event",
    cadence: "On arrival",
    owner: "Automation",
    badges: ["Lead", "Dispatch"],
    usesOpenAi: true,
    allowPromptOverride: true,
  },
  {
    key: "lead-followup-review",
    name: "Lead lifecycle follow-up",
    description:
      "Time-based AI check-ins (3 days, 1 week, 90 days, annual) that review progress and recommend the next outreach.",
    defaultPrompt:
      "After 3 days of a new lead (then at 1 week, 90 days, and annual retarget), audit what has happened, summarize changes, and propose the next best actions to re-engage or close out.",
    status: "Enabled",
    scope: "Lifecycle automations",
    trigger: "Scheduled",
    cadence: "3d / 7d / 90d / annual",
    owner: "Lifecycle",
    badges: ["Follow-up", "Retention"],
    usesOpenAi: true,
    allowPromptOverride: true,
  },
  {
    key: "operations-approval-reminders",
    name: "Operations approval reminders",
    description:
      "Scans suppliers and customers awaiting approval. Sends reminders at 12h, 3d, weekly, and monthly until actioned.",
    defaultPrompt:
      "This automation reviews pending supplier/customer approvals and notifies approvers so nothing stalls. It does not contact OpenAI.",
    status: "Enabled",
    scope: "Operations",
    trigger: "Scheduled",
    cadence: "12h / 3d / weekly / monthly",
    owner: "Operations",
    badges: ["Approvals", "Reminder"],
    usesOpenAi: false,
    allowPromptOverride: false,
  },
  {
    key: "daily-operations-summary",
    name: "Daily Operations Summary",
    description:
      "Once-per-day operations digest email summarizing pipeline, production, and queued AI work (target 6 AM MT).",
    defaultPrompt:
      "Summarize operational workload, upcoming AI jobs, and production signals into a concise, human-friendly daily digest for operations.",
    status: "Enabled",
    scope: "Operations",
    trigger: "Scheduled",
    cadence: "Daily",
    owner: "Operations",
    badges: ["Summary", "Email"],
    usesOpenAi: true,
    allowPromptOverride: false,
  },
  {
    key: "retargeting-decision",
    name: "Retargeting advisor",
    description:
      "Decide which customers, leads, and deals need follow-up right now and propose the best touchpoint.",
    defaultPrompt:
      "Continuously scan Elite Core activity and output a JSON decision about whether to contact the account now, why, and what the next best outreach should be.",
    status: "Enabled",
    scope: "Revenue operations",
    trigger: "Scheduled",
    cadence: "3d / 30d / 90d",
    owner: "Automation",
    badges: ["Retention", "Follow-up", "Revenue"],
  },
];

const InstanceAIHub = () => {
  const { instanceUniqueId, tabType } = useParams();
  const history = useHistory();
  const { authState } = useContext(AuthContext);
  const [currentTab, setCurrentTab] = useState(0);
  const [aiActions, setAiActions] = useState(defaultAiActions);
  const [actionsError, setActionsError] = useState(null);
  const [isLoadingActions, setIsLoadingActions] = useState(false);
  const [aiSettings, setAiSettings] = useState({ actions: {} });
  const [settingsError, setSettingsError] = useState(null);
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [isLoadingSettings, setIsLoadingSettings] = useState(false);
  const [settingsSaved, setSettingsSaved] = useState(false);
  const [manualEmailDrafts, setManualEmailDrafts] = useState({});
  const [copyState, setCopyState] = useState({});
  const [actionViewMode, setActionViewMode] = useState("all");
  const [testActionDialog, setTestActionDialog] = useState({
    open: false,
    action: null,
    payloadText: "",
    result: null,
    error: null,
    isSending: false,
  });

  const isAdmin = isEzeeAdministrator(authState);
  const canUseEzee = canUseEzeeAI(authState);
  const canSeeJobs = isAdmin || authState?.role === "order-rep";

  const tabConfigs = useMemo(
    () =>
      baseTabConfigs.filter((tab) => {
        if (tab.requireAdmin) return isAdmin;
        if (tab.requireOps) return canSeeJobs;
        if (tab.requireEzee) return canUseEzee;
        return true;
      }),
    [canSeeJobs, canUseEzee, isAdmin]
  );

  const actionStats = useMemo(() => {
    const total = aiActions.length;
    const usesOpenAi = aiActions.filter((action) => action.usesOpenAi).length;
    const guardrailed = aiActions.filter((action) => action.allowPromptOverride === false).length;
    const instantReady = aiActions.filter((action) => action.status === "Enabled").length;

    return { total, usesOpenAi, guardrailed, instantReady };
  }, [aiActions]);

  const personaConfigs = useMemo(() => {
    const actionsByKey = aiActions.reduce((acc, action) => {
      acc[action.key] = action;
      return acc;
    }, {});

    const pickActions = (keys) =>
      keys
        .map((key) => actionsByKey[key])
        .filter(Boolean)
        .map((action) => ({ ...action, key: action.key }));

    return [
      {
        key: "operations",
        title: "Operations persona",
        tagline: "Human-friendly ops lens that spotlights roughly a third of key automations at once.",
        actions: pickActions([
          "operations-approval-reminders",
          "daily-operations-summary",
          "lead-followup-review",
        ]),
      },
      {
        key: "sales",
        title: "Sales persona",
        tagline: "Revenue-side shortcuts that keep assignment, follow-up, and retargeting in view.",
        actions: pickActions(["lead-assignment", "retargeting-decision", "lead-followup-review"]),
      },
    ];
  }, [aiActions]);

  useEffect(() => {
    const desiredKey = tabType || "brain";
    const nextTabIndex = tabConfigs.findIndex((tab) => tab.key === desiredKey);
    setCurrentTab(nextTabIndex === -1 ? 0 : nextTabIndex);
  }, [isAdmin, tabConfigs, tabType]);

  useEffect(() => {
    if (!isAdmin) return;

    const fetchAiActions = async () => {
      setIsLoadingActions(true);
      setActionsError(null);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_NODEJS_SERVER_URL}/ai/actions`,
          {
            headers: { accessToken: localStorage.getItem("accessToken") },
          }
        );

        const payload = await response.json();
        if (payload?.success && Array.isArray(payload.actions)) {
          setAiActions(payload.actions);
        } else {
          setActionsError("Could not load AI actions right now.");
        }
      } catch (err) {
        setActionsError("Could not load AI actions right now.");
      } finally {
        setIsLoadingActions(false);
      }
    };

    fetchAiActions();
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin) return;

    const fetchAiSettings = async () => {
      setIsLoadingSettings(true);
      setSettingsError(null);
      setSettingsSaved(false);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_NODEJS_SERVER_URL}/ai/instances/${instanceUniqueId}/settings`,
          {
            headers: { accessToken: localStorage.getItem("accessToken") },
          }
        );

        const payload = await response.json();
        if (payload?.success) {
          setAiSettings(payload.settings || { actions: {} });
        } else {
          setSettingsError("Could not load AI settings right now.");
        }
      } catch (err) {
        setSettingsError("Could not load AI settings right now.");
      } finally {
        setIsLoadingSettings(false);
      }
    };

    fetchAiSettings();
  }, [instanceUniqueId, isAdmin]);

  const handleTabChange = (_e, newIndex) => {
    const tabKey = tabConfigs[newIndex]?.key || tabConfigs[0]?.key || "actions";
    setCurrentTab(newIndex);
    history.push(`/instance/${instanceUniqueId}/ai/${tabKey}`);
  };

  const renderActionBadges = (badges = []) => (
    <Stack direction="row" spacing={0.5} className="ai-badge-row">
      {badges.map((label) => (
        <Chip key={label} label={label} size="small" variant="outlined" color="default" />
      ))}
    </Stack>
  );

  const renderAiUsageChip = (usesOpenAi) => (
    <Chip
      label={usesOpenAi ? "Uses OpenAI" : "No external AI"}
      size="small"
      color={usesOpenAi ? "secondary" : "default"}
      variant={usesOpenAi ? "outlined" : "filled"}
    />
  );

  const getDefaultTestPayload = (actionKey) => {
    if (actionKey === "lead-assignment") {
      return {
        incoming: {
          businessName: "Test Co.",
          emailAddress: "lead@example.com",
          phoneNumber: "555-555-5555",
          leadSource: "Website",
          status: "New",
        },
      };
    }

    if (actionKey === "lead-followup-review") {
      return {
        customerId: 1,
        customer: {
          businessName: "Sample Prospect",
          emailAddress: "prospect@example.com",
          leadSource: "Referral",
        },
        historyEvents: [{ at: new Date().toISOString(), note: "Initial outreach sent" }],
        scheduleLabel: "manual",
      };
    }

    if (actionKey === "operations-approval-reminders") {
      return {
        context: { instanceSlug: instanceUniqueId, cadence: "manual" },
        signals: [
          { type: "email", summary: "Recent activity" },
          { type: "approval", summary: "Pending supplier and customer approvals" },
        ],
        testEmail: "info@elitedm.ca",
      };
    }

    if (actionKey === "daily-operations-summary") {
      return {
        previewOffsetDays: 3,
        testEmail: "info@elitedm.ca",
        horizonDays: 3,
        notes: "Preview what the next three days of ops focus will look like.",
      };
    }

    return {
      context: { instanceSlug: instanceUniqueId, cadence: "manual" },
      signals: [{ type: "email", summary: "Recent activity" }],
    };
  };

  const handleOpenTestDialog = (action) => {
    const defaultPayload = getDefaultTestPayload(action.key);
    setTestActionDialog({
      open: true,
      action,
      payloadText: JSON.stringify(defaultPayload, null, 2),
      result: null,
      error: null,
      isSending: false,
    });
  };

  const handleCloseTestDialog = () => {
    setTestActionDialog((prev) => ({ ...prev, open: false }));
  };

  const handleSendTestAction = async () => {
    if (!testActionDialog.action) return;
    let parsedPayload;
    try {
      parsedPayload = testActionDialog.payloadText ? JSON.parse(testActionDialog.payloadText) : {};
    } catch (err) {
      setTestActionDialog((prev) => ({ ...prev, error: "Invalid JSON payload" }));
      return;
    }

    setTestActionDialog((prev) => ({ ...prev, isSending: true, error: null, result: null }));
    try {
      const response = await fetch(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/ai/instances/${instanceUniqueId}/actions/${testActionDialog.action.key}/test`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            accessToken: localStorage.getItem("accessToken"),
          },
          body: JSON.stringify(parsedPayload),
        }
      );

      const payload = await response.json();
      if (payload?.success) {
        setTestActionDialog((prev) => ({ ...prev, result: payload.result, isSending: false }));
      } else {
        setTestActionDialog((prev) => ({
          ...prev,
          error: payload?.message || "Unable to trigger this action.",
          isSending: false,
        }));
      }
    } catch (err) {
      setTestActionDialog((prev) => ({
        ...prev,
        error: "Unable to trigger this action.",
        isSending: false,
      }));
    }
  };

  const renderActionsTable = () => (
    <Paper className="ai-table-card" elevation={2}>
      <div className="ai-table-header">
        <div>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Typography variant="h6" fontWeight={700} gutterBottom>
              AI Actions
            </Typography>
            <Tooltip
              title="Library of automations we can plug into each instance. Hover row badges for scope and trigger details."
              arrow
            >
              <InfoOutlinedIcon fontSize="small" sx={{ color: "text.secondary" }} />
            </Tooltip>
          </Stack>
          <Typography variant="body2" color="textSecondary">
            Foundation for Elite Core AI automations. These entries map to prompts, workflows, and approvals per instance.
          </Typography>
          {actionsError && (
            <Typography variant="caption" color="error">
              {actionsError}
            </Typography>
          )}
        </div>
        <Stack direction="row" spacing={1} alignItems="center">
          <ToggleButtonGroup
            size="small"
            value={actionViewMode}
            exclusive
            onChange={(_e, value) => value && setActionViewMode(value)}
          >
            <ToggleButton value="all">All actions</ToggleButton>
            <ToggleButton value="personas">Personas</ToggleButton>
          </ToggleButtonGroup>
          <Chip label="Admin only" color="primary" size="small" />
          <Chip label="Connected" size="small" variant="outlined" color="success" />
        </Stack>
      </div>

      {actionViewMode === "all" ? (
        <Table className="ai-actions-table">
            <TableHead>
              <TableRow>
                <TableCell>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Typography variant="caption" fontWeight={700} textTransform="uppercase">
                      Action
                    </Typography>
                    <Tooltip title="Name, scope, and owner for the automation." arrow>
                      <InfoOutlinedIcon fontSize="inherit" sx={{ fontSize: 14, color: "text.secondary" }} />
                    </Tooltip>
                  </Stack>
                </TableCell>
                <TableCell>Description</TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Typography variant="caption" fontWeight={700} textTransform="uppercase">
                      Trigger / Cadence
                    </Typography>
                    <Tooltip title="Whether the action runs on events or a schedule." arrow>
                      <InfoOutlinedIcon fontSize="inherit" sx={{ fontSize: 14, color: "text.secondary" }} />
                    </Tooltip>
                  </Stack>
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Typography variant="caption" fontWeight={700} textTransform="uppercase">
                      AI usage
                    </Typography>
                    <Tooltip title="Indicates if the action calls external LLMs." arrow>
                      <InfoOutlinedIcon fontSize="inherit" sx={{ fontSize: 14, color: "text.secondary" }} />
                    </Tooltip>
                  </Stack>
                </TableCell>
                <TableCell>Default prompt</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Test</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
            {isLoadingActions ? (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography variant="body2" color="textSecondary">
                    Loading AI actions...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              aiActions.map((row) => (
                <TableRow key={row.key} className="ai-actions-row">
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <div className="ai-scope-pill">{row.scope || "Lead"}</div>
                      <div>
                        <Typography fontWeight={700}>{row.name}</Typography>
                        <Typography variant="caption" color="textSecondary">
                          Owner: {row.owner || "Automation"}
                        </Typography>
                        {row.badges && row.badges.length > 0 && renderActionBadges(row.badges)}
                      </div>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{row.description}</Typography>
                  </TableCell>
                  <TableCell>
                    <Stack spacing={0.5}>
                      <Chip
                        label={row.trigger || "Event"}
                        size="small"
                        color={row.trigger === "Scheduled" ? "info" : "secondary"}
                        variant="outlined"
                      />
                      <Chip
                        label={row.cadence || "On event"}
                        size="small"
                        color="info"
                        className="ai-cadence-chip"
                      />
                    </Stack>
                  </TableCell>
                  <TableCell>{renderAiUsageChip(row.usesOpenAi)}</TableCell>
                  <TableCell>
                    <Typography variant="body2" className="ai-prompt-text">
                      {row.defaultPrompt}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={row.status || "Enabled"} size="small" color="success" />
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => handleOpenTestDialog(row)}
                    >
                      Test
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      ) : (
        <Stack spacing={2} className="ai-persona-grid">
          {personaConfigs.map((persona) => (
            <Paper key={persona.key} className="ai-persona-card" elevation={0}>
              <div className="ai-persona-glow" aria-hidden="true" />
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ xs: "flex-start", sm: "center" }}>
                <Stack direction="row" spacing={1.5} alignItems="center" className="ai-persona-heading">
                  <Avatar sx={{ bgcolor: "#0f172a", color: "#fff" }}>
                    <PersonIcon fontSize="small" />
                  </Avatar>
                  <div>
                    <Typography variant="subtitle1" fontWeight={800}>
                      {persona.title}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {persona.tagline}
                    </Typography>
                  </div>
                </Stack>
                <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
                  <Chip label={`${persona.actions.length} actions`} size="small" color="primary" variant="outlined" />
                  <Chip label="Vanity view" size="small" variant="outlined" />
                  <Chip label="Personas" size="small" color="secondary" />
                </Stack>
              </Stack>
              <Divider sx={{ my: 1.5 }} />
              <Grid container spacing={1.2}>
                {persona.actions.map((action) => (
                  <Grid item xs={12} sm={6} md={4} key={action.key}>
                    <Paper className="ai-persona-action" elevation={0}>
                      <Stack spacing={0.25}>
                        <Typography variant="subtitle2" fontWeight={700}>
                          {action.name}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {action.description}
                        </Typography>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" alignItems="center">
                          <div className="ai-scope-pill">{action.scope || "Automation"}</div>
                          {renderAiUsageChip(action.usesOpenAi)}
                          {action.badges && action.badges.length > 0 && renderActionBadges(action.badges.slice(0, 2))}
                        </Stack>
                      </Stack>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          ))}
        </Stack>
      )}
    </Paper>
  );

  const handlePromptChange = (actionKey, value) => {
    setAiSettings((prev) => ({
      ...prev,
      actions: {
        ...prev.actions,
        [actionKey]: {
          ...(prev.actions?.[actionKey] || {}),
          promptOverride: value,
        },
      },
    }));
  };

  const getManualEmailDefaults = (actionKey) => ({
    to: "",
    subject: `Retargeting outreach request for ${instanceUniqueId}`,
    body: `Hi there,\n\nCould you send a retargeting outreach email based on the latest activity? This instance is ${instanceUniqueId}.\n\nUse the AI retargeting summary to craft the message and include any at-risk quotes, proofs, or invoices so we can keep revenue moving.\n\nThanks!`,
  });

  const handleManualEmailChange = (actionKey, field, value) => {
    setManualEmailDrafts((prev) => ({
      ...prev,
      [actionKey]: { ...getManualEmailDefaults(actionKey), ...(prev[actionKey] || {}), [field]: value },
    }));
  };

  const handleCopyBody = async (actionKey) => {
    const draft = manualEmailDrafts[actionKey] || getManualEmailDefaults(actionKey);
    if (!navigator?.clipboard?.writeText) {
      setCopyState((prev) => ({ ...prev, [actionKey]: "failed" }));
      setTimeout(() => setCopyState((prev) => ({ ...prev, [actionKey]: null })), 1800);
      return;
    }
    try {
      await navigator.clipboard.writeText(draft.body || "");
      setCopyState((prev) => ({ ...prev, [actionKey]: "copied" }));
      setTimeout(() => setCopyState((prev) => ({ ...prev, [actionKey]: null })), 1800);
    } catch (err) {
      setCopyState((prev) => ({ ...prev, [actionKey]: "failed" }));
      setTimeout(() => setCopyState((prev) => ({ ...prev, [actionKey]: null })), 1800);
    }
  };

  const handleLaunchEmailClient = (actionKey) => {
    const draft = manualEmailDrafts[actionKey] || getManualEmailDefaults(actionKey);
    const to = encodeURIComponent(draft.to || "");
    const subject = encodeURIComponent(draft.subject || "Retargeting outreach request");
    const body = encodeURIComponent(draft.body || "");
    window.open(`mailto:${to}?subject=${subject}&body=${body}`, "_blank");
  };

  const handleSaveSettings = async () => {
    setIsSavingSettings(true);
    setSettingsError(null);
    setSettingsSaved(false);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/ai/instances/${instanceUniqueId}/settings`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            accessToken: localStorage.getItem("accessToken"),
          },
          body: JSON.stringify(aiSettings),
        }
      );

      const payload = await response.json();
      if (payload?.success) {
        setAiSettings(payload.settings || { actions: {} });
        setSettingsSaved(true);
      } else {
        setSettingsError("Unable to save AI settings right now.");
      }
    } catch (err) {
      setSettingsError("Unable to save AI settings right now.");
    } finally {
      setIsSavingSettings(false);
    }
  };

  const renderSettings = () => (
    <Paper className="ai-table-card" elevation={1}>
      <Stack direction="row" alignItems="center" spacing={1} mb={2}>
        <SettingsSuggestIcon fontSize="small" color="primary" />
        <Typography variant="h6" fontWeight={700}>
          AI Settings
        </Typography>
        <Tooltip title="Safe overrides that sit on top of the system prompt." arrow>
          <InfoOutlinedIcon fontSize="small" sx={{ color: "text.secondary" }} />
        </Tooltip>
      </Stack>
      <Typography variant="body2" color="textSecondary" mb={2}>
        Per-instance overrides for AI automations. Customize prompts for each action without touching the default system copy.
        Tooltips explain how each setting shapes the AI output.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <FormControlLabel
          control={<Switch checked={aiSettings?.ezeeUi?.showGlobalHeader === true} onChange={(event) => {
            setSettingsSaved(false);
            setAiSettings((previous) => ({ ...previous, ezeeUi: { ...(previous.ezeeUi || {}), showGlobalHeader: event.target.checked } }));
          }} />}
          label="Show Ask Ezee in global header"
        />
        <Typography variant="body2" color="text.secondary">
          Display the Ask Ezee shortcut for eligible users in this instance. Off by default. This only controls the global header shortcut; it does not enable or disable Ezee, its AI capabilities, or background automation.
        </Typography>
        <FormControlLabel
          control={<Switch checked={aiSettings?.ezeeUi?.hideSidebarForNonAdmins === true} onChange={(event) => {
            setSettingsSaved(false);
            setAiSettings((previous) => ({ ...previous, ezeeUi: { ...(previous.ezeeUi || {}), hideSidebarForNonAdmins: event.target.checked } }));
          }} />}
          label="Hide Ezee AI sidebar for non-administrators"
        />
        <Typography variant="body2" color="text.secondary">
          Hide the entire Ezee AI section in the left sidebar for roles below Administrator and Super Administrator. Off by default. This is a navigation preference only; it does not change direct URL access, Ezee permissions, or background automation.
        </Typography>
      </Paper>

      {settingsError && (
        <Typography variant="body2" color="error" gutterBottom>
          {settingsError}
        </Typography>
      )}

      {isLoadingSettings ? (
        <Typography variant="body2" color="textSecondary">
          Loading AI settings...
        </Typography>
      ) : (
        <Stack spacing={3} className="ai-settings-list">
          {aiActions.map((action) => {
            const override = aiSettings?.actions?.[action.key]?.promptOverride || "";
            return (
              <Paper key={action.key} className="ai-settings-card" elevation={0}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                  <div>
                    <Typography fontWeight={700}>{action.name}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {action.description}
                    </Typography>
                    <Stack direction="row" spacing={1} mt={0.5}>
                      <Chip size="small" label={action.scope} color="secondary" />
                      <Chip size="small" label={action.trigger || "Event"} variant="outlined" />
                      {action.cadence && <Chip size="small" label={action.cadence} color="info" variant="outlined" />}
                    </Stack>
                  </div>
                  <Chip size="small" label={action.owner || "Automation"} color="primary" variant="outlined" />
                </Stack>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Default prompt:
                  </Typography>
                  <Tooltip title="Base copy used when no override is provided." arrow>
                    <InfoOutlinedIcon fontSize="inherit" sx={{ fontSize: 16, color: "text.secondary" }} />
                  </Tooltip>
                </Stack>
                <Paper variant="outlined" className="ai-default-prompt">
                  <Typography variant="body2" className="ai-prompt-text">
                    {action.defaultPrompt}
                  </Typography>
                </Paper>
                <Stack direction="row" spacing={0.5} alignItems="center" mt={2}>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Override for this instance (optional):
                  </Typography>
                  <Tooltip title="Add extra guidance or constraints for this instance only." arrow>
                    <InfoOutlinedIcon fontSize="inherit" sx={{ fontSize: 16, color: "text.secondary" }} />
                  </Tooltip>
                </Stack>
                <textarea
                  className="ai-prompt-textarea"
                  placeholder="Enter custom instructions to prepend to the system prompt."
                  value={override}
                  onChange={(e) => handlePromptChange(action.key, e.target.value)}
                />
                {action.key === "retargeting-decision" && (
                  <Box className="ai-manual-email-card">
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.5 }}>
                      <Typography variant="subtitle2" fontWeight={700}>
                        Manual email send
                      </Typography>
                      <Tooltip title="Quick handoff while automation is being tuned." arrow>
                        <InfoOutlinedIcon fontSize="inherit" sx={{ fontSize: 16, color: "text.secondary" }} />
                      </Tooltip>
                    </Stack>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Use this quick launcher to manually send a retargeting outreach email while the automated workflow is being finalized.
                    </Typography>
                  <Stack spacing={1} mb={1}>
                    <TextField
                      size="small"
                      label="Recipient email"
                      placeholder="sales@customer.com"
                      value={(manualEmailDrafts[action.key] || getManualEmailDefaults(action.key)).to}
                      onChange={(e) => handleManualEmailChange(action.key, "to", e.target.value)}
                    />
                    <TextField
                      size="small"
                      label="Subject"
                      value={(manualEmailDrafts[action.key] || getManualEmailDefaults(action.key)).subject}
                      onChange={(e) => handleManualEmailChange(action.key, "subject", e.target.value)}
                    />
                    <TextField
                      size="small"
                      label="Email body"
                      multiline
                      minRows={4}
                      value={(manualEmailDrafts[action.key] || getManualEmailDefaults(action.key)).body}
                      onChange={(e) => handleManualEmailChange(action.key, "body", e.target.value)}
                    />
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Button variant="contained" size="small" onClick={() => handleLaunchEmailClient(action.key)}>
                      Open email client
                    </Button>
                    <Button variant="outlined" size="small" onClick={() => handleCopyBody(action.key)}>
                      Copy body
                    </Button>
                    {copyState[action.key] === "copied" && (
                      <Typography variant="caption" color="success.main">
                        Copied
                      </Typography>
                    )}
                    {copyState[action.key] === "failed" && (
                      <Typography variant="caption" color="error">
                        Copy failed
                      </Typography>
                    )}
                  </Stack>
                </Box>
              )}
            </Paper>
          );
        })}
          <div className="ai-settings-actions">
            <button className="ai-save-button" onClick={handleSaveSettings} disabled={isSavingSettings}>
              {isSavingSettings ? "Saving..." : "Save settings"}
            </button>
            {settingsSaved && (
              <Typography variant="body2" color="success.main">
                Saved.
              </Typography>
            )}
          </div>
        </Stack>
      )}
    </Paper>
  );

  const renderHero = () => (
    <Paper className="ai-hero" elevation={0}>
      <div className="ai-hero-left">
        <Stack direction="row" spacing={1} alignItems="center" mb={1}>
          <div className="ai-hero-beacon-pill">
            <SparkleIcon fontSize="inherit" /> AI autopilot brief
          </div>
          <Chip label={instanceUniqueId} size="small" variant="outlined" />
        </Stack>
        <Typography variant="h6" fontWeight={800} gutterBottom>
          AI mission board tuned for this instance
        </Typography>
        <Typography variant="body2" color="textSecondary" className="ai-hero-text" gutterBottom>
          Pair automation with clear guardrails. Use Actions to curate the playbook, Settings to localize prompts, and Jobs to
          see what the AI shipped, what is waiting for approval, and what needs a human nudge.
        </Typography>

        <Grid container spacing={1.5} sx={{ mt: 1 }}>
          {[
            { label: "Actions live", value: actionStats.instantReady },
            { label: "OpenAI powered", value: actionStats.usesOpenAi },
            { label: "Guardrailed", value: actionStats.guardrailed },
          ].map((metric) => (
            <Grid item xs={12} sm={4} key={metric.label}>
              <Paper className="ai-mini-card" elevation={0}>
                <Typography variant="overline" color="textSecondary">
                  {metric.label}
                </Typography>
                <Typography variant="h6" fontWeight={800}>
                  {metric.value}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Stack direction="row" spacing={1} mt={1.5} flexWrap="wrap" useFlexGap>
          <Chip label={`${actionStats.total || 0} total actions`} size="small" variant="outlined" />
          <Chip label="Human-in-the-loop ready" size="small" color="success" variant="outlined" />
          <Chip label="Persona view available" size="small" variant="outlined" />
        </Stack>

        <div className="ai-hero-ribbon">
          <TipsAndUpdatesIcon fontSize="small" />
          <div>
            <Typography variant="subtitle2" fontWeight={700}>
              Ops + Sales personas
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Jump into the Personas view to scan which automations we expect each role to own this week.
            </Typography>
          </div>
        </div>
      </div>
      <div className="ai-hero-right">
        <div className="ai-hero-badge">Mission cues</div>
        <Stack spacing={1}>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <SmartToyIcon fontSize="small" color="primary" />
            <Typography variant="body2" fontWeight={700}>
              Keep the AI specific
            </Typography>
          </Stack>
          <Typography variant="caption" color="textSecondary">
            Draft prompts sit in Settings so each instance speaks its own language without changing defaults.
          </Typography>
          <Divider flexItem />
          <Stack spacing={0.75}>
            <Stack direction="row" spacing={0.75} alignItems="center">
              <ListAltIcon fontSize="small" color="secondary" />
              <Typography variant="body2" fontWeight={700}>
                Jobs are the trail
              </Typography>
            </Stack>
            <Typography variant="caption" color="textSecondary">
              Review drafts, approvals, and retries from the Jobs tab—no guesswork about what ran.
            </Typography>
          </Stack>
        </Stack>
      </div>
    </Paper>
  );

  const renderTestActionDialog = () => (
    <Dialog open={testActionDialog.open} onClose={handleCloseTestDialog} maxWidth="sm" fullWidth>
      <DialogTitle>Test action {testActionDialog.action?.name}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Trigger this AI action manually with a sample payload. The payload will be sent to the automation runner.
        </Typography>
        <TextField
          label="Payload (JSON)"
          fullWidth
          multiline
          minRows={6}
          value={testActionDialog.payloadText}
          onChange={(e) =>
            setTestActionDialog((prev) => ({ ...prev, payloadText: e.target.value, error: null, result: null }))
          }
        />
        {testActionDialog.error && (
          <Typography variant="body2" color="error" mt={1}>
            {testActionDialog.error}
          </Typography>
        )}
        {testActionDialog.result && (
          <Paper variant="outlined" className="ai-manual-email-card" style={{ marginTop: 12 }}>
            <Typography variant="subtitle2" gutterBottom>
              Result
            </Typography>
            <pre className="ai-prompt-text">{JSON.stringify(testActionDialog.result, null, 2)}</pre>
          </Paper>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseTestDialog}>Close</Button>
        <Button variant="contained" onClick={handleSendTestAction} disabled={testActionDialog.isSending}>
          {testActionDialog.isSending ? "Sending..." : "Send test"}
        </Button>
      </DialogActions>
    </Dialog>
  );

  const currentTabKey = tabConfigs[currentTab]?.key || tabConfigs[0]?.key;

  return (
    <div className="instance-dashboard">
      <InstanceLeftSidebar />
      <div className="rightSide">
        <div className="options-container ai-hub-options-container fade-in-effect-usability">
          <div className="options-inner-container">
            <PageHeaderWithBreadcrumbs
              title="AI"
              breadcrumb="AI Hub"
              icon={<SmartToyIcon />}
              includeButton={false}
            />

            {!tabConfigs.length ? (
              <Paper className="ai-table-card" elevation={1}>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  Access restricted
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  The AI workspace is only available to administrators or authorized operations roles.
                </Typography>
              </Paper>
            ) : (
              <>
                {currentTabKey !== "brain" && renderHero()}
                <Box className="ai-hub-tabs">
                  <Tabs
                    value={currentTab}
                    onChange={handleTabChange}
                    aria-label="AI hub tabs"
                    className="ai-tabs"
                  >
                    {tabConfigs.map((tab) => (
                      <Tab
                        key={tab.key}
                        icon={tab.icon}
                        iconPosition="start"
                        label={tab.label}
                      />
                    ))}
                  </Tabs>

                  <div className="ai-tab-content">
                    {currentTabKey === "brain" && <BrainTab instanceUniqueId={instanceUniqueId} />}
                    {currentTabKey === "actions" && renderActionsTable()}
                    {currentTabKey === "settings" && <>{isAdmin && <EzeeCapabilityPanel instanceUniqueId={instanceUniqueId} />}{renderSettings()}</>}
                    {currentTabKey === "triggers" && (
                      <AiTriggersTab instanceUniqueId={instanceUniqueId} />
                    )}
                    {currentTabKey === "jobs" && <AiJobsTab instanceUniqueId={instanceUniqueId} />}
                  </div>
                </Box>
                {renderTestActionDialog()}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InstanceAIHub;
